import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {   RiShareLine, RiBookmarkLine, RiLoader4Line, RiCloseLine, RiCalendarLine,   RiNewspaperLine } from 'react-icons/ri';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config';

const News = () => {
    const [newsItems, setNewsItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [shareToast, setShareToast] = useState(null); // 'copied' or null
    const [fullImage, setFullImage] = useState(null); // URL or null

    useEffect(() => {
        fetchNews();
    }, []);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedArticle || fullImage) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [selectedArticle, fullImage]);

    const handleShare = (e, item) => {
        e.stopPropagation(); // Prevent opening modal if clicking on card share
        const shareUrl = `${window.location.origin}/news#${item.id}`;
        navigator.clipboard.writeText(shareUrl).then(() => {
            setShareToast(item.title);
            setTimeout(() => setShareToast(null), 3000);
        });
    };

    const fetchNews = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/news/read.php`);
            const data = await response.json();
            if (response.ok && data.records) {
                setNewsItems(data.records);
            }
        } catch (error) {
            console.error('Error fetching news:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white min-h-screen font-sans selection:bg-emerald-100 selection:text-emerald-900">
            {/* Editorial Header */}
            <div className="pt-40 pb-20 border-b border-slate-100/50">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-3xl"
                    >
                        <span className="text-emerald-600 font-black uppercase tracking-[0.3em] text-[10px] mb-4 block">
                            Journal & Updates
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold text-slate-950 leading-[1.1] mb-8 tracking-tight">
                            Latest from the <br />
                            <span className="text-emerald-600">Frontlines</span>
                        </h1>
                        <p className="text-lg text-slate-500 leading-relaxed font-medium">
                            Synthesizing complex ecological research into actionable conservation insights and global biodiversity developments.
                        </p>
                    </motion.div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-20">

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 gap-4">
                        <RiLoader4Line className="text-5xl text-emerald-600 animate-spin" />
                        <span className="text-slate-400 font-bold uppercase tracking-widest text-xs">Fetching Archives...</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
                        {newsItems.length > 0 ? (
                            newsItems.map((item, i) => (
                                <motion.article
                                    key={item.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    className="flex flex-col group cursor-pointer"
                                    onClick={() => setSelectedArticle(item)}
                                >
                                    {/* Elevated Image Container */}
                                    <div className="relative aspect-[4/3] mb-8 overflow-hidden rounded-2xl bg-slate-50 shadow-[0_4px_30px_rgba(0,0,0,0.03)] border border-slate-100">
                                        <img
                                            src={item.image_url || "https://images.unsplash.com/photo-1542601906990-b4d3fb7d5b1e?q=80&w=2000&auto=format&fit=crop"}
                                            alt={item.title}
                                            className="w-full h-full object-cover transition-transform duration-[2s] ease-out group-hover:scale-110"
                                        />
                                        <div className="absolute top-4 left-4">
                                            <span className="bg-white/95 backdrop-blur-md px-4 py-1.5 text-[10px] font-black text-slate-950 uppercase tracking-widest rounded-full shadow-sm border border-slate-100">
                                                {item.tag}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Refined Content */}
                                    <div className="flex flex-col flex-grow">
                                        <div className="flex items-center gap-3 text-slate-400 text-[10px] font-black uppercase tracking-widest mb-4">
                                            <RiCalendarLine size={14} className="text-emerald-500" />
                                            {new Date(item.created_at).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </div>

                                        <h3 className="text-2xl font-bold text-slate-950 mb-4 leading-[1.3] group-hover:text-emerald-700 transition-colors duration-300">
                                            {item.title}
                                        </h3>

                                        <p className="text-slate-500 text-[15px] leading-relaxed mb-8 line-clamp-3 font-medium">
                                            {item.content}
                                        </p>

                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <span className="text-[11px] font-black text-slate-950 uppercase tracking-[0.2em] group-hover:text-emerald-600 transition-colors">
                                                    Explore Article
                                                </span>
                                                <div className="h-[2px] w-8 bg-emerald-100 group-hover:w-16 group-hover:bg-emerald-500 transition-all duration-500" />
                                            </div>
                                            <button
                                                onClick={(e) => handleShare(e, item)}
                                                className="p-2 text-slate-300 hover:text-emerald-600 transition-colors"
                                                title="Copy Link"
                                            >
                                                <RiShareLine size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.article>
                            ))
                        ) : (
                            <div className="col-span-full text-center py-40 border-2 border-dashed border-slate-100 rounded-[3rem]">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-50 rounded-full mb-6">
                                    <RiNewspaperLine className="text-3xl text-slate-300" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-2">No updates found</h3>
                                <p className="text-slate-400">Our team is currently synthesizing new research data.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Premium Light Modal */}
            <AnimatePresence>
                {selectedArticle && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedArticle(null)}
                            className="absolute inset-0 bg-slate-950/40 backdrop-blur-xl"
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 30 }}
                            transition={{ type: "spring", damping: 30, stiffness: 300 }}
                            className="relative w-full max-w-6xl max-h-[92vh] bg-white rounded-[1rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col border border-slate-100"
                        >
                            {/* Sticky Header for Modal */}
                            <div className="absolute top-8 right-8 z-30">
                                <button
                                    onClick={() => setSelectedArticle(null)}
                                    className="w-12 h-12 bg-white/95 backdrop-blur-md shadow-xl rounded-full flex items-center justify-center text-slate-500 hover:text-red-500 transition-all active:scale-95"
                                >
                                    <RiCloseLine size={24} />
                                </button>
                            </div>

                            {/* Scrollable Container */}
                            <div className="overflow-y-auto px-8 md:px-16 pt-16 pb-20 custom-scrollbar">
                                <div className="max-w-3xl mx-auto">
                                    <div className="flex items-center gap-4 mb-8">
                                        <span className="bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border border-emerald-100">
                                            {selectedArticle.tag}
                                        </span>
                                        <div className="h-px flex-1 bg-slate-100" />
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                            Published {new Date(selectedArticle.created_at).toLocaleDateString()}
                                        </span>
                                    </div>

                                    <h2 className="text-4xl md:text-6xl font-bold text-slate-950 mb-12 leading-[1.15] tracking-tight">
                                        {selectedArticle.title}
                                    </h2>

                                    {/* Featured Image */}
                                    <div
                                        onClick={() => setFullImage(selectedArticle.image_url || "https://images.unsplash.com/photo-1542601906990-b4d3fb7d5b1e?q=80&w=2000&auto=format&fit=crop")}
                                        className="aspect-[21/10] rounded-[1rem] overflow-hidden mb-16 shadow-2xl relative group/img cursor-zoom-in"
                                    >
                                        <img
                                            src={selectedArticle.image_url || "https://images.unsplash.com/photo-1542601906990-b4d3fb7d5b1e?q=80&w=2000&auto=format&fit=crop"}
                                            alt={selectedArticle.title}
                                            className="w-full h-full object-cover transition-transform duration-[3s] group-hover/img:scale-110"
                                        />
                                        <div className="absolute inset-0 ring-1 ring-inset ring-black/10 rounded-[1rem]" />
                                        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/10 transition-colors flex items-center justify-center">
                                            <span className="opacity-0 group-hover/img:opacity-100 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-900 shadow-xl transition-all translate-y-4 group-hover/img:translate-y-0">
                                                View Full Image
                                            </span>
                                        </div>
                                    </div>

                                    {/* Article Body */}
                                    <div className="prose prose-slate max-w-none">
                                        <p className="text-xl text-slate-600 leading-[1.8] font-medium whitespace-pre-wrap selection:bg-emerald-100">
                                            {selectedArticle.content}
                                        </p>
                                    </div>

                                    {/* Social Connect */}
                                    <div className="mt-20 pt-10 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-8">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-emerald-900/20">
                                                C
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-slate-900">COE Research Team</p>
                                                <p className="text-xs text-slate-400 font-medium tracking-wide">Conservation Outreach & Education</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-4">
                                            <button
                                                onClick={(e) => handleShare(e, selectedArticle)}
                                                className="flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-bold text-xs uppercase tracking-widest hover:bg-slate-800 transition-colors shadow-xl transition-all active:scale-95"
                                            >
                                                <RiShareLine size={18} />
                                                Share Insight
                                            </button>
                                            <button className="w-12 h-12 rounded-full border border-slate-100 flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:border-emerald-100 transition-all">
                                                <RiBookmarkLine size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Image Lightbox */}
            <AnimatePresence>
                {fullImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-2xl flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
                        onClick={() => setFullImage(null)}
                    >
                        <button
                            className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors p-4"
                            onClick={() => setFullImage(null)}
                        >
                            <RiCloseLine size={40} />
                        </button>

                        <motion.img
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            src={fullImage}
                            alt="Full View"
                            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl shadow-black/50"
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Share Feedback Toast */}
            <AnimatePresence>
                {shareToast && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.9 }}
                        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[100] bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 border border-white/10 backdrop-blur-md"
                    >
                        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                            <RiShareLine size={16} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400">Link Copied!</p>
                            <p className="text-xs font-bold text-slate-300 truncate max-w-[200px]">{shareToast}</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <Footer />
        </div>
    );
};

export default News;
